import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, GuardsCheckEnd, Router } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { filter } from 'rxjs';

/** Racine des pages de l'espace personnel d'un collaborateur qui est aussi une ressource. */
export const PERSONAL_SPACE_ROOT = '/mon-espace';

/** Page de la vue société -> même page dans l'espace personnel. */
const PERSONAL_PAGES: [string, string][] = [
    ['/cra/', `${PERSONAL_SPACE_ROOT}/cra/`],
    ['/leaves/', `${PERSONAL_SPACE_ROOT}/conges/`],
    ['/apps/historique-cra', `${PERSONAL_SPACE_ROOT}/historique-cra`],
    ['/apps/note-de-frais', `${PERSONAL_SPACE_ROOT}/note-de-frais`],
    ['/apps/ca-suivi', `${PERSONAL_SPACE_ROOT}/ca-suivi`],
    ['/resources/documents', `${PERSONAL_SPACE_ROOT}/fiche/documents`],
];

/**
 * Point de vue des pages partagées entre la société et ses ressources (CRA, congés, notes de frais,
 * documents) : la vue société, ou la vue « ressource » d'une ressource ou d'un collaborateur dans son
 * espace personnel (routes marquées {@code data.personalSpace}).
 */
@Injectable({ providedIn: 'root' })
export class WorkspaceService {
    private _authService = inject(AuthService);
    private _router = inject(Router);
    private _personalRoute = false;

    constructor() {
        // Fixé avant la création des composants de la page cible.
        this._router.events
            .pipe(filter((event): event is GuardsCheckEnd => event instanceof GuardsCheckEnd && event.shouldActivate))
            .subscribe((event) => (this._personalRoute = WorkspaceService._isPersonal(event.state.root)));
    }

    /** La page s'affiche pour la propre fiche ressource de la personne connectée. */
    actsAsResource(): boolean {
        if (this._authService.isCompany()) {
            return this._personalRoute && this._authService.hasPersonalSpace();
        }
        return this._authService.isResource();
    }

    /** La page s'affiche pour toute la société (propriétaire ou collaborateur hors de son espace personnel). */
    actsAsCompany(): boolean {
        return this._authService.isCompany() && !this.actsAsResource();
    }

    /** Identifiant de l'acteur affiché : sa fiche ressource, sinon l'identifiant métier du compte. */
    actorId(): string | null {
        if (this.actsAsResource()) {
            const resourceId = this._authService.getMyResourceId();
            return resourceId != null ? String(resourceId) : null;
        }
        return this._authService.getUserIdFromToken();
    }

    /** Lien vers une page partagée, dans l'espace personnel si l'on y est. */
    route(path: string): string {
        if (!this._personalRoute) {
            return path;
        }
        const page = PERSONAL_PAGES.find(([companyPath]) => path.startsWith(companyPath));
        return page ? page[1] + path.substring(page[0].length) : path;
    }

    private static _isPersonal(snapshot: ActivatedRouteSnapshot | null): boolean {
        for (let route = snapshot; route; route = route.firstChild) {
            if (route.routeConfig?.data?.['personalSpace']) {
                return true;
            }
        }
        return false;
    }
}
